import { useReducer, useEffect, useState } from 'react';

export type State<T> =
  | {
      state: 'pending';
      data?: T;
    }
  | {
      state: 'error';
      errorMessage: string | undefined;
    }
  | {
      state: 'ok';
      data: T;
    };

type Action<T> =
  | {
      type: 'loading';
      data?: T;
    }
  | {
      type: 'failed';
      errorMessage?: string;
    }
  | {
      type: 'ok';
      data: T;
    };

export function usePromiseFunction<A extends any[], T>(
  fn: (...args: A) => Promise<T>,
  ...args: A
) {
  function stateReducer(state: State<T>, action: Action<T>): State<T> {
    if (action.type === 'loading') {
      return {
        ...state,
        state: 'pending',
        data: action.data
      };
    }
    if (action.type === 'failed') {
      return {
        ...state,
        state: 'error',
        errorMessage: action.errorMessage
      };
    }
    if (action.type === 'ok') {
      return {
        ...state,
        state: 'ok',
        data: action.data
      };
    }

    return state;
  }

  const [state, dispatch] = useReducer(stateReducer, { state: 'pending' });
  useEffect(() => {
    dispatch({ type: 'loading' });
    fn(...args)
      .then(data => {
        dispatch({ type: 'ok', data: data });
      })
      .catch(error => {
        dispatch({ type: 'failed', errorMessage: error.message });
      });
  }, [...args, fn]);

  return state;
}

export function useDebounce(value: string, delay: number): string {
  const [debounceQuery, setDebounceQuery] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebounceQuery(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debounceQuery;
}

export function useScrollIntoViewOnMount(
  resultsRef: React.MutableRefObject<any>
) {
  useEffect(() => {
    if (resultsRef.current) {
      window.scrollTo(0, resultsRef.current.offsetTop);
    }
  }, [resultsRef]);
}
